import {
  differenceInDays,
  differenceInWeeks,
  format,
  formatDistanceToNowStrict,
  isToday,
  isYesterday,
  parseISO,
} from "date-fns";

/**
 * Formats a date for metadata display in report headers (e.g., "Dec 21 - 2:00pm")
 * @param date - Date object or ISO string to format
 * @returns Formatted string like "Dec 21 - 2:00pm"
 */
export const formatMetadataDate = (date: Date | string): string => {
  const parsedDate = typeof date === "string" ? parseISO(date) : date;
  return format(parsedDate, "MMM d - h:mmaaa");
};

/**
 * Formats a date based on how many days ago it was relative to today
 * @param date - The date to format (Date object or ISO string)
 * @returns "today", "N days ago", or "dd/MM/yyyy" for dates over a year old
 */
export default function formatDateBasedOnDays(date: Date | string): string {
  const today = new Date();
  const parsedDate = typeof date === "string" ? parseISO(date) : date;
  const daysDifference = differenceInDays(today, parsedDate);

  if (daysDifference < 1) {
    return "today";
  }

  if (daysDifference <= 365) {
    return `${daysDifference} days ago`;
  }

  return format(parsedDate, "dd/MM/yyyy");
}

/**
 * Formats an ISO date string as a human-readable relative time
 * @param dateString - ISO date string to format
 * @returns Relative time string (e.g., "5 minutes ago", "Yesterday", "N day(s) ago", or "MMM d, yyyy")
 */
export const formatTimeRelative = (dateString: string) => {
  const date = parseISO(dateString);

  if (isToday(date)) {
    const distance = formatDistanceToNowStrict(date, { addSuffix: false });
    return `${distance} ago`;
  }

  if (isYesterday(date)) {
    return "Yesterday";
  }

  const daysAgo = differenceInDays(new Date(), date);

  if (daysAgo < 30) {
    return `${daysAgo} day${daysAgo !== 1 ? "s" : ""} ago`;
  }

  return format(date, "MMM d, yyyy");
};

/**
 * Formats a date for report display with uppercase relative time
 * @param date - Date object or ISO string to format
 * @returns Uppercase relative time string (e.g., "TODAY", "YESTERDAY", "N DAYS AGO", "LAST WEEK", "N WEEKS AGO", or "MMM dd, yyyy")
 */
export const formatReportDisplayTime = (date: Date | string) => {
  const now = new Date();
  const parsedDate = typeof date === "string" ? parseISO(date) : date;

  if (isToday(parsedDate)) return "TODAY";
  if (isYesterday(parsedDate)) return "YESTERDAY";

  const days = differenceInDays(now, parsedDate);

  if (days <= 6) return `${days} DAYS AGO`;
  if (days <= 13) return "LAST WEEK";
  if (days <= 30) {
    const weeks = differenceInWeeks(now, parsedDate);
    return `${weeks} WEEKS AGO`;
  }
  return format(parsedDate, "MMM dd, yyyy");
};
